import { FC, useId } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { BusyLabel, LineGlyph } from "src/domains/shared/components/StateTransition";
import {
  MYPAGE_DIALOG_ACTION_MIN_WIDTH_PX,
  MYPAGE_DIALOG_ACTIONS_SX,
  MYPAGE_DIALOG_PAPER_SX,
} from "../myPageStyles";

interface LogoutConfirmDialogProps {
  open: boolean;
  signingOut: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * 로그아웃 확인 창 — 멤버 삭제 창과 같은 규격(`myPageStyles` 의 창 상수)을 쓰되, 데이터를 지우는 동작이 아니라 주 색상으로 둔다.
 * 로그아웃 진행 중(자리 해제 최대 3초)에는 닫을 수 없다.
 */
export const LogoutConfirmDialog: FC<LogoutConfirmDialogProps> = ({ open, signingOut, onCancel, onConfirm }) => {
  const { t } = useTranslation("projects");
  const titleId = useId();

  return (
    <Dialog
      open={open}
      onClose={signingOut ? undefined : onCancel}
      aria-labelledby={titleId}
      PaperProps={{ sx: MYPAGE_DIALOG_PAPER_SX }}
    >
      <ModalHeader
        title={t("myPage.logout", { ns: "projects" })}
        titleId={titleId}
        size="small"
        onClose={() => !signingOut && onCancel()}
      />
      {/* 보조 문구 없이 한 줄이라 아이콘 원과 가운데를 맞춘다. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 20px" }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: COLOR_CONSTANTS.primary.main,
            backgroundColor: alpha(COLOR_CONSTANTS.primary.main, 0.08),
          }}
        >
          <LineGlyph name="logout" size={16} />
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.45, color: "rgba(0, 0, 0, 0.85)" }}>
          {t("myPageModal.logout.message", { ns: "projects" })}
        </Typography>
      </Box>
      <Box sx={MYPAGE_DIALOG_ACTIONS_SX}>
        <Button size="small" emphasis="outlined" intent="inherit" disabled={signingOut} onClick={onCancel}>
          {t("constants.cancel", { ns: "common" })}
        </Button>
        <Button
          size="small"
          emphasis="contained"
          intent="primary"
          onClick={() => !signingOut && onConfirm()}
          sx={{ minWidth: MYPAGE_DIALOG_ACTION_MIN_WIDTH_PX }}
        >
          <BusyLabel
            label={t("myPage.logout", { ns: "projects" })}
            busy={signingOut}
            busyLabel={t("myPageModal.logout.signingOut", { ns: "projects" })}
            spinnerSize={12}
          />
        </Button>
      </Box>
    </Dialog>
  );
};
